import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import SeletorDeTurma, { MARCA, SugestaoEnturmacao } from '../turmas/SeletorDeTurma';

/* ─── Tipos ─── */
interface Ocupacao {
  id_turma: number;
  nome: string;
  faixa_etaria: string;
  turno: string;
  capacidade_efetiva: number;
  limite_por_educador: number | null;
  limite_por_m2: number | null;
  ocupadas: number;
  ociosas: number;
  excedido: boolean;
}
interface FilaItem {
  id: number;
  aluno_nome: string;
  data_nascimento?: string | null;
  responsavel_nome?: string;
  responsavel_telefone?: string;
  turma_pretendida?: string;
  id_turma_pretendida?: number | null;
  turno_pretendido?: string | null;
  origem?: string;
  status: string;
  /** `null` = saiu da fila (matriculado/desistente): a tela mostra "—". */
  posicao: number | null;
  pontuacao_prioridade: number;
  vulnerabilidade_social?: boolean;
  proximidade_km?: number | null;
}

/** O formulário de inscrição que a creche preenche ao telefone. */
interface NovaInscricao {
  aluno_nome: string;
  data_nascimento: string;
  responsavel_nome: string;
  responsavel_telefone: string;
  responsavel_email: string;
  turno_pretendido: string;
  id_turma_pretendida: number | null;
  vulnerabilidade_social: boolean;
  proximidade_km: string;
  observacoes: string;
}

const INSCRICAO_VAZIA: NovaInscricao = {
  aluno_nome: '',
  data_nascimento: '',
  responsavel_nome: '',
  responsavel_telefone: '',
  responsavel_email: '',
  turno_pretendido: '',
  id_turma_pretendida: null,
  vulnerabilidade_social: false,
  proximidade_km: '',
  observacoes: '',
};
interface Matricula {
  id: number;
  status: string;
  ano_letivo: number;
  aluno?: { nome?: string };
  turma?: { nome?: string } | null;
  cadastro_convite_enviado_em?: string | null;
  cadastro_completo_em?: string | null;
}
interface ConviteCadastro {
  url: string;
  email_destino: string | null;
  email_enviado: boolean;
  validade_dias: number;
}

const EMERALD = '#10b981';

/**
 * Rótulos de origem da inscrição. A entidade documenta 'publico'/'interno', mas
 * há dado gravado com 'site'/'indicacao' — e a tela mostrava tudo que não fosse
 * 'publico' como "Interna", inclusive quem veio do site.
 */
const ORIGENS: Record<string, { label: string; bg: string; cor: string }> = {
  publico: { label: 'Pública', bg: '#ecfdf5', cor: '#047857' },
  site: { label: 'Site', bg: '#ecfdf5', cor: '#047857' },
  interno: { label: 'Interna', bg: '#f1f5f9', cor: '#475569' },
  indicacao: { label: 'Indicação', bg: '#eff6ff', cor: '#1d4ed8' },
};
const card: React.CSSProperties = {
  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14,
  padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,.06)',
};
const btn = (bg: string): React.CSSProperties => ({
  background: bg, color: '#fff', border: 'none', borderRadius: 8,
  padding: '8px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13,
});
const tag = (bg: string, color: string): React.CSSProperties => ({
  background: bg, color, padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700,
});
const campo: React.CSSProperties = {
  width: '100%', padding: '9px 10px', border: '1px solid #cbd5e1',
  borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
};
const rotulo: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4,
};
const modalFundo: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
};

export default function DemandaPage() {
  const [ocupacao, setOcupacao] = useState<Ocupacao[]>([]);
  const [fila, setFila] = useState<FilaItem[]>([]);
  const [matriculas, setMatriculas] = useState<Matricula[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);
  const [convite, setConvite] = useState<{ matricula: Matricula; dados: ConviteCadastro } | null>(null);
  const [inscricao, setInscricao] = useState<NovaInscricao | null>(null);
  const [salvandoInscricao, setSalvandoInscricao] = useState(false);
  /** Quem está sendo enturmado agora, e a turma escolhida para ele. */
  const [enturmar, setEnturmar] = useState<{
    item: FilaItem;
    idTurma: number | null;
    sugestao: SugestaoEnturmacao | null;
  } | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [oc, fi, ma] = await Promise.all([
        api.get('/turmas/ocupacao'),
        api.get('/academico/fila'),
        api.get('/matriculas'),
      ]);
      setOcupacao(Array.isArray(oc.data) ? oc.data : []);
      setFila(Array.isArray(fi.data) ? fi.data : []);
      setMatriculas(Array.isArray(ma.data) ? ma.data : []);
    } catch (e: any) {
      setMsg({ tipo: 'erro', texto: 'Falha ao carregar dados de demanda.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  async function acao(fn: () => Promise<any>, sucesso: string) {
    try {
      await fn();
      setMsg({ tipo: 'ok', texto: sucesso });
      await carregar();
    } catch (e: any) {
      setMsg({ tipo: 'erro', texto: e?.response?.data?.message || 'Operação falhou.' });
    }
  }

  /**
   * Avisa o responsável de que a vaga existe e gera o link para ele finalizar o
   * cadastro. Mostra o link na tela também: se o e-mail não sair (ou o
   * responsável não tiver e-mail), a creche manda por WhatsApp.
   */
  async function convidarResponsavel(m: Matricula) {
    try {
      const { data } = await api.post<ConviteCadastro>(`/matriculas/${m.id}/convite-cadastro`);
      setConvite({ matricula: m, dados: data });
      setMsg({
        tipo: 'ok',
        texto: data.email_enviado
          ? `Convite enviado para ${data.email_destino}.`
          : 'Link gerado — envie ao responsável (o e-mail não pôde ser enviado).',
      });
      await carregar();
    } catch (e: any) {
      setMsg({ tipo: 'erro', texto: e?.response?.data?.message || 'Não foi possível gerar o convite.' });
    }
  }

  async function efetivar(m: Matricula) {
    if (!m.cadastro_completo_em) {
      const ok = window.confirm(
        `O responsável ainda NÃO finalizou o cadastro de ${m.aluno?.nome || 'a criança'}.\n\n` +
        'Sem esses dados (CPF, endereço, qualificação) o contrato de matrícula não pode ser gerado.\n\n' +
        'Efetivar mesmo assim?',
      );
      if (!ok) return;
    }
    await acao(() => api.post(`/matriculas/${m.id}/efetivar`), 'Matrícula efetivada.');
  }

  function fmtDataHora(iso?: string | null) {
    return iso ? new Date(iso).toLocaleString('pt-BR') : '';
  }

  /**
   * Inscreve na fila pela tela. Até aqui a creche só recebia inscrição pela
   * página pública ou convertendo visita do CRM — o telefone que toca, que é o
   * caso mais comum, não tinha caminho nenhum.
   */
  async function salvarInscricao() {
    if (!inscricao) return;
    setSalvandoInscricao(true);
    try {
      await api.post('/academico/fila', {
        ...inscricao,
        // Campo em branco não vira dado: mandar '' gravaria vazio onde o certo
        // é não haver informação.
        data_nascimento: inscricao.data_nascimento || undefined,
        responsavel_email: inscricao.responsavel_email || undefined,
        turno_pretendido: inscricao.turno_pretendido || undefined,
        id_turma_pretendida: inscricao.id_turma_pretendida ?? undefined,
        proximidade_km: inscricao.proximidade_km === '' ? undefined : Number(inscricao.proximidade_km),
        observacoes: inscricao.observacoes || undefined,
      });
      setInscricao(null);
      setMsg({ tipo: 'ok', texto: 'Inscrição registrada na fila de espera.' });
      await carregar();
    } catch (e: any) {
      setMsg({ tipo: 'erro', texto: e?.response?.data?.message || 'Não foi possível inscrever.' });
    } finally {
      setSalvandoInscricao(false);
    }
  }

  /**
   * Matricula quem foi chamado, na turma escolhida.
   *
   * ⚖️ Turma incompatível **não é bloqueada** — a creche tem exceções que o
   * sistema não conhece (irmãos, laudo, decisão pedagógica). O que a tela faz é
   * não deixar passar em silêncio: mostra o motivo e pede uma confirmação.
   */
  async function confirmarMatricula() {
    if (!enturmar?.idTurma) return;
    const { item, idTurma, sugestao } = enturmar;
    if (sugestao?.aderencia === 'incompativel') {
      const ok = window.confirm(
        `${sugestao.resumo}\n\n` +
        `Matricular ${item.aluno_nome} nesta turma mesmo assim?\n` +
        'A decisão é da creche — o sistema registra e acompanha.',
      );
      if (!ok) return;
    }
    setEnturmar(null);
    await acao(
      () => api.post(`/academico/fila/${item.id}/matricular`, { id_turma: idTurma }),
      'Matrícula criada — efetive abaixo.',
    );
  }

  if (loading) return <div style={{ padding: 24 }}>Carregando…</div>;

  const nomeTurmaPorId = new Map(ocupacao.map((o) => [o.id_turma, o.nome]));

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 28 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, color: '#0f172a' }}>Demanda & Matrículas</h1>
        <p style={{ margin: '4px 0 0', color: '#64748b' }}>
          Ocupação das turmas, fila de espera priorizada e efetivação de matrículas.
        </p>
      </div>

      {msg && (
        <div style={{
          ...card, padding: '12px 16px',
          borderLeft: `4px solid ${msg.tipo === 'ok' ? EMERALD : '#ef4444'}`,
          color: msg.tipo === 'ok' ? '#065f46' : '#991b1b',
        }}>
          {msg.texto}
        </div>
      )}

      {/* ── Ocupação ── */}
      <section>
        <h2 style={{ fontSize: 18, color: '#0f172a' }}>Painel de Ocupação</h2>
        {ocupacao.length === 0 ? (
          <p style={{ color: '#64748b' }}>Nenhuma turma cadastrada ainda.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
            {ocupacao.map((o) => (
              <div key={o.id_turma} style={{ ...card, borderTop: `3px solid ${o.excedido ? '#ef4444' : EMERALD}` }}>
                <strong style={{ color: '#0f172a' }}>{o.nome}</strong>
                <div style={{ fontSize: 12, color: '#64748b', margin: '2px 0 10px' }}>{o.turno}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                  <span>Ocupadas: <b>{o.ocupadas}</b></span>
                  <span>Ociosas: <b>{o.ociosas}</b></span>
                </div>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>
                  Capacidade efetiva: <b>{o.capacidade_efetiva || '—'}</b>
                </div>
                {o.excedido && (
                  <div style={{ ...tag('#fef2f2', '#dc2626'), display: 'inline-block', marginTop: 8 }}>
                    Limite legal excedido
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Fila de espera ── */}
      <section>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 18, color: '#0f172a' }}>Fila de Espera</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btn('#2563eb')} onClick={() => setInscricao({ ...INSCRICAO_VAZIA })}>
              Inscrever na fila
            </button>
            <button style={btn(EMERALD)} onClick={() => acao(() => api.post('/academico/fila/recalcular-prioridade'), 'Prioridades recalculadas.')}>
              Recalcular prioridade
            </button>
          </div>
        </div>
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                <th style={{ padding: 12 }}>#</th>
                <th style={{ padding: 12 }}>Criança</th>
                <th style={{ padding: 12 }}>Turma pretendida</th>
                <th style={{ padding: 12 }}>Origem</th>
                <th style={{ padding: 12 }}>Pontuação</th>
                <th style={{ padding: 12 }}>Status</th>
                <th style={{ padding: 12 }}></th>
              </tr>
            </thead>
            <tbody>
              {fila.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 16, color: '#64748b' }}>Fila vazia.</td></tr>
              ) : fila.map((f) => (
                <tr key={f.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: 12 }}>{f.posicao ?? '—'}</td>
                  <td style={{ padding: 12 }}>{f.aluno_nome}{f.vulnerabilidade_social && <span style={{ ...tag('#fff7ed', '#ea580c'), marginLeft: 8 }}>vulnerável</span>}</td>
                  <td style={{ padding: 12 }}>
                    {(f.id_turma_pretendida && nomeTurmaPorId.get(f.id_turma_pretendida)) || f.turma_pretendida || '—'}
                  </td>
                  <td style={{ padding: 12 }}>
                    {(() => {
                      const o = ORIGENS[String(f.origem || '')];
                      // Valor desconhecido aparece como ele é, em vez de virar
                      // "Interna": rótulo errado é pior que rótulo estranho.
                      return o
                        ? <span style={tag(o.bg, o.cor)}>{o.label}</span>
                        : <span style={tag('#fef9c3', '#854d0e')}>{f.origem || '—'}</span>;
                    })()}
                  </td>
                  <td style={{ padding: 12 }}>{Number(f.pontuacao_prioridade || 0).toFixed(1)}</td>
                  <td style={{ padding: 12 }}>{f.status}</td>
                  <td style={{ padding: 12, textAlign: 'right', display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                    {f.status === 'pendente' && (
                      <>
                        <button style={btn('#f97316')} onClick={() => acao(() => api.post(`/academico/fila/${f.id}/chamar`), 'Inscrito chamado.')}>Chamar</button>
                        <button style={btn('#ef4444')} onClick={() => acao(() => api.patch(`/academico/fila/${f.id}/status`, { status: 'desistente' }), 'Inscrição rejeitada.')}>Rejeitar</button>
                      </>
                    )}
                    {f.status === 'chamado' && (
                      // A turma pretendida é o ponto de partida, não a decisão:
                      // ela pode estar lotada ou fora da idade, e antes disso
                      // ninguém conferia. O modal mostra a enturmação sugerida.
                      <button
                        style={btn(EMERALD)}
                        onClick={() => setEnturmar({
                          item: f,
                          idTurma: f.id_turma_pretendida ?? null,
                          sugestao: null,
                        })}
                      >
                        Matricular…
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Matrículas ── */}
      <section>
        <h2 style={{ fontSize: 18, color: '#0f172a' }}>Matrículas</h2>
        <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: 13 }}>
          Antes de confirmar, convide o responsável a finalizar o cadastro: ele recebe um link,
          preenche os próprios dados (CPF, endereço, qualificação) e a creche então efetiva.
        </p>
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                <th style={{ padding: 12 }}>ID</th>
                <th style={{ padding: 12 }}>Criança</th>
                <th style={{ padding: 12 }}>Turma</th>
                <th style={{ padding: 12 }}>Ano</th>
                <th style={{ padding: 12 }}>Status</th>
                <th style={{ padding: 12 }}>Cadastro do responsável</th>
                <th style={{ padding: 12 }}></th>
              </tr>
            </thead>
            <tbody>
              {matriculas.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 16, color: '#64748b' }}>Nenhuma matrícula.</td></tr>
              ) : matriculas.map((m) => (
                <tr key={m.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: 12 }}>{m.id}</td>
                  <td style={{ padding: 12 }}>{m.aluno?.nome || '—'}</td>
                  <td style={{ padding: 12 }}>{m.turma?.nome || '—'}</td>
                  <td style={{ padding: 12 }}>{m.ano_letivo}</td>
                  <td style={{ padding: 12 }}>
                    <span style={tag(m.status === 'matriculado' ? '#d1fae5' : '#f1f5f9', m.status === 'matriculado' ? '#065f46' : '#475569')}>{m.status}</span>
                  </td>
                  <td style={{ padding: 12 }}>
                    {m.cadastro_completo_em ? (
                      <span style={tag('#d1fae5', '#065f46')} title={`Concluído em ${fmtDataHora(m.cadastro_completo_em)}`}>
                        ✓ completo
                      </span>
                    ) : m.cadastro_convite_enviado_em ? (
                      <span style={tag('#fef9c3', '#854d0e')} title={`Convite enviado em ${fmtDataHora(m.cadastro_convite_enviado_em)}`}>
                        aguardando responsável
                      </span>
                    ) : (
                      <span style={tag('#f1f5f9', '#475569')}>não convidado</span>
                    )}
                  </td>
                  <td style={{ padding: 12, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {m.status !== 'cancelado' && !m.cadastro_completo_em && (
                      <button
                        style={{ ...btn('#2563eb'), marginRight: 8 }}
                        onClick={() => convidarResponsavel(m)}
                      >
                        {m.cadastro_convite_enviado_em ? 'Reenviar convite' : 'Convidar responsável'}
                      </button>
                    )}
                    {m.status === 'em_analise' && (
                      <button style={btn(EMERALD)} onClick={() => efetivar(m)}>Efetivar</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Inscrever na fila (o telefone que toca) ── */}
      {inscricao && (
        <div style={modalFundo} onClick={() => setInscricao(null)}>
          <div
            style={{ ...card, width: 720, maxWidth: '96vw', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, color: '#0f172a' }}>Inscrever na fila de espera</h3>
            <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
              Para quem procura a creche por telefone ou na porta. A posição na fila sai da
              pontuação (idade, vulnerabilidade e proximidade) — não da ordem de digitação.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
              <div>
                <label style={rotulo}>Nome da criança *</label>
                <input
                  style={campo}
                  value={inscricao.aluno_nome}
                  onChange={(e) => setInscricao({ ...inscricao, aluno_nome: e.target.value })}
                />
              </div>
              <div>
                <label style={rotulo}>Data de nascimento</label>
                <input
                  type="date"
                  style={campo}
                  value={inscricao.data_nascimento}
                  onChange={(e) => setInscricao({ ...inscricao, data_nascimento: e.target.value })}
                />
              </div>
              <div>
                <label style={rotulo}>Nome do responsável *</label>
                <input
                  style={campo}
                  value={inscricao.responsavel_nome}
                  onChange={(e) => setInscricao({ ...inscricao, responsavel_nome: e.target.value })}
                />
              </div>
              <div>
                <label style={rotulo}>Telefone do responsável *</label>
                <input
                  style={campo}
                  value={inscricao.responsavel_telefone}
                  onChange={(e) => setInscricao({ ...inscricao, responsavel_telefone: e.target.value })}
                />
              </div>
              <div>
                <label style={rotulo}>E-mail do responsável</label>
                <input
                  type="email"
                  style={campo}
                  value={inscricao.responsavel_email}
                  onChange={(e) => setInscricao({ ...inscricao, responsavel_email: e.target.value })}
                />
              </div>
              <div>
                <label style={rotulo}>Turno pretendido</label>
                <select
                  style={campo}
                  value={inscricao.turno_pretendido}
                  onChange={(e) => setInscricao({ ...inscricao, turno_pretendido: e.target.value })}
                >
                  <option value="">Não informado</option>
                  <option value="matutino">Matutino</option>
                  <option value="vespertino">Vespertino</option>
                  <option value="integral">Integral</option>
                </select>
              </div>
              <div>
                <label style={rotulo}>Distância até a creche (km)</label>
                <input
                  type="number"
                  min={0}
                  step="0.1"
                  style={campo}
                  value={inscricao.proximidade_km}
                  onChange={(e) => setInscricao({ ...inscricao, proximidade_km: e.target.value })}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#334155' }}>
                  <input
                    type="checkbox"
                    checked={inscricao.vulnerabilidade_social}
                    onChange={(e) => setInscricao({ ...inscricao, vulnerabilidade_social: e.target.checked })}
                  />
                  Situação de vulnerabilidade social
                </label>
              </div>
            </div>

            <div style={{ marginTop: 14 }}>
              <label style={rotulo}>Turma pretendida (opcional)</label>
              <SeletorDeTurma
                dataNascimento={inscricao.data_nascimento}
                turnoPretendido={inscricao.turno_pretendido}
                valor={inscricao.id_turma_pretendida}
                onChange={(id) => setInscricao((atual) => (atual ? { ...atual, id_turma_pretendida: id } : atual))}
                textoVazio="Sem turma definida ainda"
              />
            </div>

            <div style={{ marginTop: 14 }}>
              <label style={rotulo}>Observações</label>
              <textarea
                style={{ ...campo, minHeight: 70, resize: 'vertical' }}
                value={inscricao.observacoes}
                onChange={(e) => setInscricao({ ...inscricao, observacoes: e.target.value })}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button style={btn('#94a3b8')} onClick={() => setInscricao(null)}>Cancelar</button>
              <button
                style={btn(EMERALD)}
                disabled={salvandoInscricao}
                onClick={salvarInscricao}
              >
                {salvandoInscricao ? 'Inscrevendo…' : 'Inscrever'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Enturmação: qual turma serve para esta criança, e por quê ── */}
      {enturmar && (
        <div style={modalFundo} onClick={() => setEnturmar(null)}>
          <div style={{ ...card, width: 640, maxWidth: '96vw' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, color: '#0f172a' }}>
              Matricular — {enturmar.item.aluno_nome}
            </h3>
            <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
              As turmas vêm ordenadas pela que mais serve. A escolha continua sendo da creche:
              nenhuma opção fica bloqueada.
            </p>

            {enturmar.item.id_turma_pretendida && (
              <p style={{ fontSize: 13, color: '#475569' }}>
                A família pretendia{' '}
                <b>{nomeTurmaPorId.get(enturmar.item.id_turma_pretendida) || 'uma turma que não existe mais'}</b>
                {enturmar.item.turno_pretendido ? ` · turno ${enturmar.item.turno_pretendido}` : ''}.
              </p>
            )}

            <SeletorDeTurma
              dataNascimento={enturmar.item.data_nascimento}
              turnoPretendido={enturmar.item.turno_pretendido}
              valor={enturmar.idTurma}
              onChange={(id, sugestao) =>
                setEnturmar((atual) => (atual ? { ...atual, idTurma: id, sugestao } : atual))
              }
            />

            {enturmar.sugestao?.aderencia === 'incompativel' && (
              <div style={{ marginTop: 12, fontSize: 13, color: MARCA.incompativel.cor }}>
                Esta turma está fora da faixa etária. Dá para seguir — o sistema vai pedir uma
                confirmação e registrar a decisão.
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button style={btn('#94a3b8')} onClick={() => setEnturmar(null)}>Cancelar</button>
              <button style={btn(EMERALD)} disabled={!enturmar.idTurma} onClick={confirmarMatricula}>
                Matricular
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Link do convite — para copiar e mandar por WhatsApp se preciso */}
      {convite && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setConvite(null)}>
          <div style={{ ...card, width: 560, maxWidth: '92vw' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, color: '#0f172a' }}>
              Convite — {convite.matricula.aluno?.nome || 'criança'}
            </h3>
            <p style={{ fontSize: 14, color: '#475569', marginTop: 0 }}>
              {convite.dados.email_enviado
                ? `E-mail enviado para ${convite.dados.email_destino}.`
                : convite.dados.email_destino
                  ? `Não foi possível enviar o e-mail para ${convite.dados.email_destino}. Mande o link abaixo ao responsável.`
                  : 'O responsável não tem e-mail cadastrado. Mande o link abaixo (WhatsApp, por exemplo).'}
            </p>
            <p style={{ fontSize: 13, color: '#64748b' }}>
              O link vale por {convite.dados.validade_dias} dias e abre o formulário do responsável
              legal e financeiro.
            </p>
            <input
              readOnly
              value={convite.dados.url}
              onFocus={(e) => e.currentTarget.select()}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button
                style={btn('#334155')}
                onClick={() => {
                  navigator.clipboard?.writeText(convite.dados.url);
                  setMsg({ tipo: 'ok', texto: 'Link copiado.' });
                }}
              >
                Copiar link
              </button>
              <button style={btn(EMERALD)} onClick={() => setConvite(null)}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
